<template>
    <v-dialog v-model="editUserDialog" max-width="767" persistent>
        <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" @click="setUser()" :color="successColor" icon>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                            mdi-pencil
                        </v-icon>
                    </template>
                    <span>Edit</span>
                </v-tooltip>
            </v-btn>
        </template>
        <v-card>
            <v-card-title>
                Edit Residence Assistant
            </v-card-title>
            <v-card-text>
                <v-form ref="editUserForm" v-model="valid" lazy-validation>
                    <v-text-field v-model="user.cn" label="Name" :rules="nameRules" required>
                    </v-text-field>
                    <v-text-field v-model="user.username" counter="8" disabled
                        hint="Please provide only the Western ID. Do not include @uwo.ca" label="Western Mail"
                        persistent-hint :rules="mailRules" suffix="@uwo.ca" required>
                    </v-text-field>
                    <v-select v-model="user.gender" :items="genderItems" label="Gender"
                        placeholder="Please select..." required :rules="genderRules">
                    </v-select>
                    <v-text-field v-model="user.prefpronoun" label="Preferred Pronoun" :rules="pronounRules">
                    </v-text-field>
                    <v-select v-model="user.ratype" :items="raItems" label="Residence Assistant Type" :rules="raRules"
                        required>
                    </v-select>
                    <v-select v-model="user.building" hide-details :items="getBuildings" label="Building">
                    </v-select>
                    <v-text-field v-model="user.room" hide-details label="Room">
                    </v-text-field>
                    <v-container fluid>
                        <v-row>
                            <v-col cols="12" md="6" class="py-0">
                                <v-menu ref="startDate" v-model="startDateMenu" :close-on-content-click="false"
                                    transition="scale-transition" offset-y max-width="290px" min-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field v-model="user.startDate" id="startDate" :color="successColor"
                                            label="Start Date" name="startDate" prepend-icon="mdi-calendar-check"
                                            readonly :rules="startDateRules" v-bind="attrs" v-on="on">
                                        </v-text-field>
                                    </template>
                                    <v-date-picker v-model="user.startDate" :color="successColor" :max="user.endDate"
                                        no-title @input="startDateMenu = false">
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12" md="6" class="py-0">
                                <v-menu ref="endDate" v-model="endDateMenu" :close-on-content-click="false"
                                    transition="scale-transition" offset-y max-width="290px" min-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field v-model="user.endDate" id="endDate" :color="dangerColor"
                                            label="End Date" name="endDate" prepend-icon="mdi-calendar-remove" readonly
                                            :rules="endDateRules" v-bind="attrs" v-on="on">
                                        </v-text-field>
                                    </template>
                                    <v-date-picker v-model="user.endDate" :color="dangerColor" :min="user.startDate"
                                        no-title @input="endDateMenu = false">
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12" class="pt-0">
                                <v-checkbox v-model="user.active" dense false-value="0"
                                    hint="This active control will override the start date and end date" label="Active"
                                    persistent-hint true-value="1"></v-checkbox>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn ref="confirmBtn" class="white--text" :color="successColor" :disabled="!valid"
                    @click="editUser()">
                    Save
                </v-btn>
                <v-btn :color="warningColor" @click="closeDialog()">
                    Cancel
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'edit-user',
    props: {
        buildings: {
            required: true,
            type: Array
        },
        item: {
            required: true,
            type: Object
        }
    },
    data() {
        return {
            editUserDialog: false,
            endDateMenu: false,
            endDateRules: [
                v => !!v || 'End date is required',
            ],
            genderItems: [{
                    text: 'Female',
                    value: 'F'
                },
                {
                    text: 'Male',
                    value: 'M'
                },
                {
                    text: 'Other',
                    value: 'Other'
                },
                {
                    text: 'Non-binary',
                    value: 'NonBinary'
                }
            ],
            genderRules: [
                v => !!v || 'Gender is required'
            ],
            pronounRules: [
                v => !!v || 'Pronoun is required',
                v => /^([A-Za-z]+)\/([A-Za-z]+)$/.test(v) || 'Pronoun should include forward slash (/)',
            ],
            mailRules: [
                v => !!v || 'Western mail is required',
                v => (v && v.length >= 2 && v.length <= 8) || 'Western mail must be valid',
            ],
            nameRules: [
                v => !!v || 'Name is required',
                v => (v && v.length >= 2) || 'Name must be more than 2 characters',
            ],
            raItems: [{
                    disabled: true,
                    text: 'Please select...',
                    value: ''
                }, {
                    text: 'Residence Assistant',
                    value: 'residence assistant'
                },
                {
                    text: 'Head Residence Assistant',
                    value: 'head residence assistant'
                },
                {
                    text: 'House Coordinator',
                    value: 'house coordinator'
                }
            ],
            raRules: [
                v => !!v || 'Residence Assistant type is required'
            ],
            startDateMenu: false,
            startDateRules: [
                v => !!v || 'Start date is required',
            ],
            user: '',
            valid: true
        }
    },
    methods: {
        closeDialog() {
            this.editUserDialog = false;
        },
        editUser() {
            const that = this;
            const formData = new FormData();
            let api = `https://${location.hostname}/cgi-bin/res-app-api/controller.api.pl?setRA`;


            if (this.$refs.editUserForm.validate()) {
                // Parse array into string
                formData.append('mode', 'setRA');
                formData.append('data', JSON.stringify(this.user));
                that.$refs.confirmBtn.loading = true;

                // Post request
                fetch(api, {
                        method: 'POST',
                        body: formData
                    })
                    .then(function (response) {
                        // Return JSON response
                        return response.json();
                    })
                    .then(function (json) {
                        // Success result
                        if (json.result.match(/OK/i)) {
                            that.$emit('setNotification', '#1dc9b7', 'mdi-account-check', `The Residence Assistant ${that.user.cn} has been successfully updated.`, true);
                        } else if (json.message.match(/UNAUTHORIZED/i)) {
                            that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `You are not authorized to perform this action. Please refresh your application.`, true);
                        } else {
                            that.$emit('setNotification', '#ffb822', 'mdi-account-alert', `The Residence Assistant ${that.user.cn} could not be updated. ${json.message}.`, true);
                        }

                        that.$emit('refreshData');
                        that.$refs.confirmBtn.loading = false;
                        that.closeDialog();
                    })
                    .catch(function () {
                        that.$emit('refreshData');
                        that.$refs.confirmBtn.loading = false;
                        that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `An error occurred. Please contact your system administrator.`, true);
                    });
            }
        },
        setUser() {
            this.user = {
                ...this.item
            };
        }
    },
    computed: {
        dangerColor() {
            return '#f64e60';
        },
        getBuildings() {
            let buildingsList = [];

            // Push empty buildings value
            buildingsList.push({
                text: 'None',
                value: ''
            });

            // Parse the buildings array
            for (let i = 0; i < this.buildings.length; i++) {
                if (this.buildings[i].name != "_buildingsInit") {
                    buildingsList.push({
                        text: this.buildings[i].name,
                        value: this.buildings[i].name
                    });
                }
            }

            // Sort array
            buildingsList.sort((a, b) => a.value.localeCompare(b.value));

            return buildingsList;
        },
        primaryColor() {
            return '#5867dd';
        },
        successColor() {
            return '#1dc9b7';
        },
        warningColor() {
            return '#ffb822'
        }
    }
}
</script>

<style scoped>
.v-input--checkbox {
    margin-top: 0;
}
</style>